import { createContext, useContext, useState, useCallback, useEffect } from "react";
import translations from "@/lib/translations";
import { detectFromBrowser, detectFromIP } from "@/lib/detectLanguage";
import api from "@/lib/api";

const LanguageContext = createContext(null);
const STORAGE_KEY = "padel_lang";

function persistToBackend(lang) {
  // Fire-and-forget; only if a token exists. Avoids noisy 401s on the login page.
  if (typeof window === "undefined") return;
  if (!localStorage.getItem("padel_token")) return;
  api.put("/me/language", { lang }).catch(() => {});
}

export function LanguageProvider({ children }) {
  // First load: honour an explicit user choice from a previous visit. Otherwise
  // start with a synchronous best-guess from the browser locale, then upgrade
  // asynchronously using IP geolocation.
  const [lang, setLangState] = useState(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "es" || stored === "en") return stored;
    return detectFromBrowser() || "es";
  });
  // `userPicked` becomes true once the user explicitly toggles — after which
  // auto-detection (IP / phone / email) NO LONGER overrides their choice.
  const [userPicked, setUserPicked] = useState(() => !!localStorage.getItem(STORAGE_KEY));

  // One-time IP geolocation upgrade on app load if the user hasn't picked yet.
  useEffect(() => {
    if (userPicked) return;
    let cancelled = false;
    detectFromIP().then((detected) => {
      if (cancelled || !detected) return;
      if (!localStorage.getItem(STORAGE_KEY)) setLangState(detected);
    });
    return () => { cancelled = true; };
  }, [userPicked]);

  const setLang = useCallback((next, opts = {}) => {
    const valid = ["es", "en", "ru", "zh", "ar", "de", "fr", "hi", "it", "ja", "ko", "pt", "tr"];
    if (!valid.includes(next)) return;
    setLangState(next);
    if (opts.persist) {
      localStorage.setItem(STORAGE_KEY, next);
      setUserPicked(true);
      persistToBackend(next);
    }
  }, []);

  const toggleLang = useCallback(() => {
    setLangState((prev) => {
      const next = prev === "es" ? "en" : "es";
      localStorage.setItem(STORAGE_KEY, next);
      setUserPicked(true);
      persistToBackend(next);
      return next;
    });
  }, []);

  // Translations only exist for es/en today. For any other selection we fall
  // back to English copy automatically — the value of `lang` still drives
  // backend persistence and UI-side icon hints.
  const t = useCallback((path) => {
    const keys = path.split(".");
    const map = translations[lang] || translations.en;
    let val = map;
    for (const k of keys) {
      if (!val) {
        // fall back to English for missing keys
        let alt = translations.en;
        for (const kk of keys) {
          if (!alt) return path;
          alt = alt[kk];
        }
        return alt || path;
      }
      val = val[k];
    }
    if (val === undefined || val === null) {
      let alt = translations.en;
      for (const kk of keys) {
        if (!alt) return path;
        alt = alt[kk];
      }
      return alt || path;
    }
    return val;
  }, [lang]);

  return (
    <LanguageContext.Provider value={{ lang, toggleLang, setLang, t, userPicked }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLang() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLang must be inside LanguageProvider");
  return ctx;
}
